import GeneralPromptStyles from 'common/Prose/Prompts/Prompt/Prompt.module.scss'
import { type EditorView } from 'prosemirror-view'
import { useCallback, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import Styles from './TableSizePrompt.module.scss'

const MIN_SIZE = 5

interface TableSizeInfo {
  rows: number
  columns: number
}

interface Props {
  maxSize: number
  onSubmit: (tableSize: TableSizeInfo) => void
  onCancel: () => void
}

const PromptContent = ({ maxSize, onSubmit, onCancel }: Props) => {
  const [shownRowCount, setShownRowCount] = useState(MIN_SIZE)
  const [shownColumnCount, setShownColumnCount] = useState(MIN_SIZE)
  const [activeRow, setActiveRow] = useState(2)
  const [activeColumn, setActiveColumn] = useState(2)
  const isBoxActive = (rowIndex: number, columnIndex: number) => {
    return rowIndex <= activeRow && columnIndex <= activeColumn
  }

  const recalculateRowsShown = useCallback(
    (rowIndex: number) => {
      let newShownRowCount = rowIndex + 2
      if (newShownRowCount > maxSize) newShownRowCount = maxSize
      if (newShownRowCount < MIN_SIZE) newShownRowCount = MIN_SIZE
      if (newShownRowCount !== shownRowCount) setShownRowCount(newShownRowCount)
    },
    [maxSize, shownRowCount],
  )

  const recalculateColumnsShown = useCallback(
    (columnIndex: number) => {
      let newShownColumnCount = columnIndex + 2
      if (newShownColumnCount > maxSize) newShownColumnCount = maxSize
      if (newShownColumnCount < MIN_SIZE) newShownColumnCount = MIN_SIZE
      if (newShownColumnCount !== shownColumnCount) setShownColumnCount(newShownColumnCount)
    },
    [maxSize, shownColumnCount],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onSubmit({ rows: activeRow + 1, columns: activeColumn + 1 })
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        const newColumnIndex = activeColumn + 1
        recalculateColumnsShown(newColumnIndex)
        if (activeColumn < maxSize - 1) setActiveColumn(newColumnIndex)
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        const newColumnIndex = activeColumn - 1
        recalculateColumnsShown(newColumnIndex)
        if (activeColumn > 0) setActiveColumn(newColumnIndex)
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const newRowIndex = activeRow + 1
        recalculateRowsShown(newRowIndex)
        if (activeRow < maxSize - 1) setActiveRow(newRowIndex)
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        const newRowIndex = activeRow - 1
        recalculateRowsShown(newRowIndex)
        if (activeRow > 0) setActiveRow(newRowIndex)
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
    }
  }, [onSubmit, activeColumn, activeRow, recalculateRowsShown, recalculateColumnsShown, maxSize, onCancel])

  const renderBox = (rowIndex: number, columnIndex: number) => {
    return (
      <div
        className={isBoxActive(rowIndex, columnIndex) ? Styles.tableSizeButton__active : Styles.tableSizeButton}
        key={columnIndex}
        onMouseEnter={() => {
          recalculateRowsShown(rowIndex)
          recalculateColumnsShown(columnIndex)
          setActiveRow(rowIndex)
          setActiveColumn(columnIndex)
        }}
        onClick={() => onSubmit({ rows: activeRow + 1, columns: activeColumn + 1 })}
      />
    )
  }

  const renderRow = (rowIndex: number) => {
    return (
      <div className={Styles.rowContainer} key={rowIndex}>
        {[...Array(shownColumnCount)].map((empty, columnIndex) => renderBox(rowIndex, columnIndex))}
      </div>
    )
  }

  return (
    <div className={Styles.tableSizeContent}>
      {[...Array(shownRowCount)].map((empty, rowIndex) => renderRow(rowIndex))}
      <div className={Styles.sizeText}>
        {activeColumn + 1} x {activeRow + 1}
      </div>
    </div>
  )
}

export const openTableSizePrompt = async (
  view: EditorView,
  position: { left: number; top: number },
  maxSize: number,
  fixed?: boolean,
) => {
  const requiredWidth = 26 + maxSize * 18
  const requiredHeight = 52 + maxSize * 18
  const windowHeight = window.innerHeight
  const windowWidth = window.innerWidth

  if (windowHeight - position.top < requiredHeight) {
    position.top = windowHeight - requiredHeight - 100
  }
  if (windowWidth - position.left < requiredWidth) {
    position.left = windowWidth - requiredWidth
  }

  const wrapper = document.createElement('div')
  wrapper.className = GeneralPromptStyles.promptWrapper
  wrapper.style.top = position.top + 'px'
  wrapper.style.left = position.left + 'px'
  if (fixed) wrapper.style.position = 'fixed'
  document.body.appendChild(wrapper)
  const root = createRoot(wrapper)

  const mouseOutside = (e: MouseEvent) => {
    if (!(e.target instanceof Node) || !wrapper.contains(e.target)) close()
  }
  setTimeout(() => window.addEventListener('mousedown', mouseOutside), 50)

  let resolveRef: any

  const closePromise = new Promise<TableSizeInfo>((resolve) => {
    resolveRef = resolve
  })

  const close = (tableSize?: TableSizeInfo) => {
    window.removeEventListener('mousedown', mouseOutside)
    root.unmount()
    wrapper.remove()
    view.focus()
    if (tableSize) {
      resolveRef(tableSize)
    }
  }

  root.render(<PromptContent maxSize={maxSize} onSubmit={(tableSize) => close(tableSize)} onCancel={close} />)

  return closePromise
}
